import { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useFinchConnect } from '@tryfinch/react-connect';
import { StoreFinchServiceToken, CreateFinchSession } from 'services/server';
import { TokenContext } from 'pages/TokenRequired';
import { ImportType } from '../lib/interfaces';
import { FINCH_PAYROLL_SYSTEMS } from '../lib/constants';
import { datadogLogs } from '@datadog/browser-logs';

const logger = datadogLogs.createLogger('ConnectFinch');

export interface ConnectPayrollDisplayProps {
  openFinch: () => void;
  loading: boolean;
  finchError: string;
}

interface ConnectFinchProps {
  onPayrollConnected: () => Promise<void>;
  connectPayrollDisplay: (props: ConnectPayrollDisplayProps) => JSX.Element;
  payrollSystem?: string;
  importType: ImportType;
  taxYear?: number;
}

export const ConnectFinch = ({
  connectPayrollDisplay,
  onPayrollConnected,
  payrollSystem,
  importType,
  taxYear,
}: ConnectFinchProps) => {
  const tokenContext = useContext(TokenContext);

  const [finchError, setFinchError] = useState('');
  const [finchOpen, setFinchOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hasOpenedFinch, setHasOpenedFinch] = useState<boolean>(false);

  const payrollProvider = payrollSystem ? payrollSystem.toLowerCase() : '';
  const mode = 'employer';
  const manual = true;
  const sandbox = process.env.REACT_APP_CONNECTION_NAME !== 'prod';

  const onSuccess = useCallback(
    ({ code }: { code: string }) => {
      // Ensure this is the correct instance responding to the Finch callback
      if (!finchOpen) {
        return;
      }
      setFinchOpen(false);

      const email = tokenContext.email || undefined;
      const token = tokenContext.token || undefined;

      StoreFinchServiceToken(code, importType, token, email, taxYear).then(
        ({ errorMsg }) => {
          if (errorMsg) {
            logger.error(
              `Error storing Finch token - ${
                payrollSystem ? payrollSystem.toLowerCase() : ''
              } : ${errorMsg}`,
            );
            setFinchError(errorMsg);
            return;
          }

          return onPayrollConnected().finally(() => setLoading(false));
        },
      );
    },
    [
      finchOpen,
      tokenContext.email,
      tokenContext.token,
      importType,
      taxYear,
      payrollSystem,
      onPayrollConnected,
    ],
  );

  const onError = useCallback(
    ({ errorMessage }: { errorMessage: string }) => {
      setFinchError(errorMessage);
      setLoading(false);
      setFinchOpen(false);
      setHasOpenedFinch(false);

      logger.error(
        `Error connecting Finch - ${payrollProvider} : ${errorMessage}`,
      );
    },
    [payrollProvider],
  );
  const onClose = useCallback(() => {
    setLoading(false);
    setFinchOpen(false);
    setHasOpenedFinch(false);
  }, []);

  // Use useMemo to recreate the hook configuration when sessionId changes
  const finchConfig = useMemo(
    () => ({
      sessionId: sessionId || undefined,
      onSuccess,
      onError,
      onClose,
      sandbox,
      mode,
      manual,
    }),
    [sessionId, onSuccess, onError, onClose, sandbox, mode, manual],
  );

  const { open } = useFinchConnect(finchConfig);

  // Watch for sessionId changes and open Finch when ready
  useEffect(() => {
    if (sessionId && finchOpen && !hasOpenedFinch) {
      const timer = setTimeout(() => {
        try {
          setHasOpenedFinch(true);
          open();
        } catch (error) {
          setFinchError('Failed to open Finch Connect');
          setLoading(false);
          setFinchOpen(false);
          setHasOpenedFinch(false);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [sessionId, finchOpen, hasOpenedFinch, open]);

  /**
   * NEW: Finch Connect now requires that we create a session before opening it.
   * This function handles the creation of the session and sets the sessionId in
   * state, which will trigger the useEffect, update the sessionId passed into the
   * useFinchConnect hook, then open Finch.
   */
  const connectWithFinch = () => {
    if (!sessionId) {
      const createSession = async () => {
        try {
          setLoading(true);
          setFinchOpen(true);
          setHasOpenedFinch(false);

          if (!payrollSystem?.trim()) {
            setFinchError('Please select a payroll provider before connecting');
            setLoading(false);
            setFinchOpen(false);
            return;
          }

          const payrollProviderForSession = payrollSystem.toLowerCase().trim();

          // Find the payroll system in FINCH_PAYROLL_SYSTEMS to get the auth method
          const payrollConfig = FINCH_PAYROLL_SYSTEMS.find(
            (system) => system.id === payrollProviderForSession,
          );

          if (!payrollConfig) {
            setFinchError(`Unsupported payroll provider: ${payrollSystem}`);
            setLoading(false);
            setFinchOpen(false);
            return;
          }

          // Use the first auth method from the array
          const authMethod = payrollConfig.auth_method[0];

          const result = await CreateFinchSession(
            payrollProviderForSession,
            authMethod,
          );

          if (result.errorMsg) {
            setFinchError(result.errorMsg);
            setLoading(false);
            setFinchOpen(false);
            return;
          }

          setSessionId(result.data?.session_id || null);
        } catch (error) {
          logger.error(`Error creating Finch session: ${error}`);
          setFinchError('Failed to create connection session');
          setLoading(false);
          setFinchOpen(false);
        }
      };

      createSession();
    } else {
      setLoading(true);
      setFinchOpen(true);
      setHasOpenedFinch(false);
    }
  };

  return connectPayrollDisplay({
    loading,
    openFinch: connectWithFinch,
    finchError,
  });
};
